// 多言語対応テキストを管理するためのフック
// windowオブジェクトから翻訳データを取得し、コンポーネントで利用しやすくする

import type { WakoFlowTranslations } from '../types/translations';

export const useTranslations = (): WakoFlowTranslations => {
  // window.wakoFlowTranslations からデータを取得
  const translations = window.wakoFlowTranslations;
  
  // 現在の言語を取得（複数の方法でフォールバック）
  const windowLocale = window.wakoFlowLocale;
  const pathname = window.location.pathname;
  const urlBasedLanguage = pathname.startsWith('/en/') ? 'en' : 'ja';
  
  // 日本以外は英語の判定ロジック
  let currentLocale: 'ja' | 'en';
  if (windowLocale === 'ja') {
    currentLocale = 'ja';
  } else if (windowLocale) {
    // 日本語以外のlocaleが設定されている場合は英語
    currentLocale = 'en';
  } else {
    // localeが未設定の場合はURLから判定
    currentLocale = urlBasedLanguage;
  }
  
  // フォールバック用のデフォルト翻訳
  const defaultTranslations: WakoFlowTranslations = {
    // 日本語用デフォルト
    flowLandingDescription: 'WAKO COFFEE LAB.で、あなたのカフェやブランドにぴったりのオリジナルコーヒーを形にしましょう。',
    flowLandingDescriptionSp: 'WAKO COFFEE LAB.で、\nあなたのカフェやブランドに\nぴったりのオリジナルコーヒーを\n形にしましょう。',
    flowLandingDescriptionPc: 'WAKO COFFEE LAB.で、あなたのカフェやブランドに\nぴったりのオリジナルコーヒーを形にしましょう。',
    flowLandingNotice: '※オリジナルデザインで作成する場合は事前にデザインデータのご用意をお願いします。',
    flowLandingUsageGuideLink: 'ご利用方法を詳しくみる',
    flowLandingOrderStartButton: 'ORDER START',
    logoAlt: 'WAKO COFFEE LAB.',
    step1Title: '加工の種類を選択',
    step2Title: 'コーヒー豆の種類を選択',
    stepIndicator_step1_sp: '加工形態の\n選択',
    stepIndicator_step1_pc: '加工形態の選択',
    stepIndicator_step2_sp: 'コーヒー豆を\n選択',
    stepIndicator_step2_pc: 'コーヒー豆を選択',
    stepIndicator_step3_sp: 'パッケージを\n選択',
    stepIndicator_step3_pc: 'パッケージを選択',
    stepIndicator_step4_sp: '発注数の\n選択',
    stepIndicator_step4_pc: '発注数の選択',
    stepIndicator_step5_sp: 'デザイン\n入稿',
    stepIndicator_step5_pc: 'デザイン入稿',

    // SelectProcessType
    selectProcessType_title: '加工形態の選択',
    selectProcessType_option1_label: 'フリーズドライ',
    selectProcessType_option1_desc: 'フリーズドライの説明が入ります。フリーズドライの説明が入ります。',
    selectProcessType_option2_label: '濃縮液',
    selectProcessType_option2_desc: '濃縮液の説明が入ります。濃縮液の説明が入ります。',
    selectProcessType_option3_label: 'ストレートコーヒー',
    selectProcessType_option3_desc: 'ストレートコーヒーの説明が入ります。ストレートコーヒーの説明が入ります。',

    // SelectCoffeeBean
    selectCoffeeBean_title: 'コーヒー豆を選択',
    selectCoffeeBean_option1_label: '自分の豆を使う',
    selectCoffeeBean_option1_desc: 'お客様が普段使用されているコーヒー豆をお送りください。',
    selectCoffeeBean_option2_label: 'WAKOの豆を使う',
    selectCoffeeBean_option2_desc: 'WAKOで取り扱っているコーヒー豆からお選びください。',
    selectCoffeeBean_cardSelector_title: '銘柄を選択する',
    selectCoffeeBean_varietyTitle: '銘柄を選択',
    selectCoffeeBean_bean1_label: 'ハウスブレンド',
    selectCoffeeBean_bean1_price: '（328JPY/100gあたり）',
    selectCoffeeBean_bean1_desc: 'ブラジルとコロンビアのみで味づくりを行い、あっさりと飲みやすく「毎日飲んでも飽きがこない」をテーマにした、味価格共にデイリーユース向けのブレンドコーヒーです。',
    selectCoffeeBean_bean2_label: 'ハウスダークブレンド',
    selectCoffeeBean_bean2_price: '（328JPY/100gあたり）',
    selectCoffeeBean_bean2_desc: '香ばしい香りに、苦みを含んだ豊かなコクが特徴の深煎りのブレンドコーヒーです。',
    
    // パッキング種類選択（濃縮液・ストレート用）
    selectPackage_title: 'リキッドの種類',
    selectPackage_spout_label: 'スパウト',
    selectPackage_spout_desc: '軽量で持ち運びやすいスパウト付きパウチタイプです。',
    selectPackage_individualPackaging_label: '個包装',
    selectPackage_individualPackaging_desc: '個別包装で持ち運びやすいタイプです。',
    selectPackage_bottle_label: 'ボトル',
    selectPackage_bottle_desc: '密閉性に優れたボトルタイプです。',
    selectPackage_region_restriction_notice: '※一部地域では配送に制限があります。',
    
    // インナーパッケージ選択（フリーズドライ用）
    selectInnerPackage_title: 'インナーパッケージを選択',
    selectInnerPackage_stick_label: 'スティック',
    selectInnerPackage_stick_desc: '個包装のスティックタイプです。',
    selectInnerPackage_cube_label: 'キューブ',
    selectInnerPackage_cube_desc: 'コンパクトなキューブタイプです。',
    
    // 砂糖濃度選択（濃縮液・ストレート用）
    selectSugar_title: '砂糖濃度を選択',
    selectSugar_none_label: '無糖',
    selectSugar_none_desc: '砂糖を使用しません。',
    selectSugar_3_label: '3%',
    selectSugar_3_desc: 'ほんのり甘い微糖タイプです。',
    selectSugar_6_label: '6%',
    selectSugar_6_desc: 'やや甘めのタイプです。',
    selectSugar_9_label: '9%',
    selectSugar_9_desc: 'バランスの良い甘さです。',
    selectSugar_12_label: '12%',
    selectSugar_12_desc: 'しっかりとした甘さです。',
    selectSugar_18_label: '18%',
    selectSugar_18_desc: '甘めのタイプです。',
    selectSugar_24_label: '24%',
    selectSugar_24_desc: '高糖度タイプです。',
    
    // 価格表示
    priceDisplay_basePrice: 'ベース価格',
    priceDisplay_optionPrice: 'オプション価格',
    priceDisplay_totalPrice: '合計価格',
    priceDisplay_currency: '¥',
    priceDisplay_taxIncluded: '税込',
    priceDisplay_perLot: 'ロット',
    
    // 共通アクション
    action_next: '次へ',
    action_back: '戻る',
    action_confirm: '確認',
    action_addToCart: 'カートに追加',
    action_select: '選択',
    action_skip: 'スキップ',
    
    // エラーメッセージ
    error_selectRequired: 'コーヒー豆の種類を選択してください',
    error_selectVariety: '銘柄を選択してください',
    error_selectInnerPackage: 'インナーパッケージを選択してください',
    error_selectPackage: 'パッキング種類を選択してください',
    error_selectSugar: '砂糖濃度を選択してください',
    error_fileRequired: 'ファイルが必要です',
    error_invalidFile: '無効なファイルです',
    error_priceCalculation: '価格計算エラーが発生しました',
    
    // 成功メッセージ
    success_addedToCart: 'カートに追加しました',
    success_fileUploaded: 'ファイルをアップロードしました',
    
    // フォーム関連
    form_selectOption: 'オプションを選択',
    form_uploadFile: 'ファイルをアップロード',
    form_fileName: 'ファイル名',
    form_fileSize: 'ファイルサイズ',
    
    // パッケージタイプ
    packageType_stick: 'スティック',
    packageType_cube: 'キューブ',
    packageType_spout: 'スパウト',
    packageType_bottle: 'ボトル',
    packageType_individualPackaging: '個包装',
    
    // STEP3: パッケージオプション選択
    selectPackageOptions_title: 'パッケージオプションの選択',
    
    // フリーズドライ用 - インナーパッケージ関連
    selectInnerDesign_title: 'インナーパッケージのデザイン',
    selectInnerDesign_required_label: '必要',
    selectInnerDesign_required_desc: 'オリジナルデザインを使用します',
    selectInnerDesign_notRequired_label: '不要',
    selectInnerDesign_notRequired_desc: '標準デザインを使用します',
    
    selectInnerFrontLabel_title: 'インナーパッケージの表ラベルは必要ですか？',
    selectInnerFrontLabel_required_label: '必要',
    selectInnerFrontLabel_required_desc: '表ラベルを追加します',
    selectInnerFrontLabel_notRequired_label: '不要',
    selectInnerFrontLabel_notRequired_desc: '表ラベルは使用しません',
    
    // 外箱関連（全フロー共通）
    selectOuterBox_title: '外箱',
    selectOuterBox_required_label: '必要',
    selectOuterBox_required_desc: '外箱を追加します',
    selectOuterBox_notRequired_label: '不要',
    selectOuterBox_notRequired_desc: '外箱は使用しません',
    
    selectOuterBoxDesign_title: '外箱デザイン',
    selectOuterBoxDesign_required_label: '必要',
    selectOuterBoxDesign_required_desc: 'オリジナルデザインを使用します',
    selectOuterBoxDesign_notRequired_label: '不要',
    selectOuterBoxDesign_notRequired_desc: '標準デザインを使用します',
    
    // 濃縮液・ストレート用 - 表ラベル関連
    selectFrontLabel_title: '表ラベル',
    selectFrontLabel_required_label: '必要',
    selectFrontLabel_required_desc: '表ラベルを追加します',
    selectFrontLabel_notRequired_label: '不要',
    selectFrontLabel_notRequired_desc: '表ラベルは使用しません',
    
    // 価格表示（オプション用）
    priceDisplay_optionAdditional: '追加料金',
    priceDisplay_estimatedPrice: '概算価格',
    
    // STEP4: ロット数選択
    selectLotSize_title: '発注ロット数',
    selectLotSize_description: 'ご希望のロット数を選択してください。ロット数により価格が変わります。',
    selectLotSize_notice: '※1ロット=100個',
    selectLotSize_saveNotice1: '※「保存して次へ」を押すとここまでの内容は保存されます。',
    selectLotSize_saveNotice2: '以降、TOPページから続きの作成ができます。',
    priceDisplay_priceOnRequest: '価格はお問い合わせ',
    
    // 新しく追加する翻訳データ（日本語）
    
    // エラーメッセージ（追加）
    error_uploadInnerDesign: 'インナーデザインファイルをアップロードしてください',
    error_uploadFrontLabel: '表ラベルファイルをアップロードしてください',
    error_uploadOuterBoxDesign: '外箱デザインファイルをアップロードしてください',
    error_uploadBackLabel: '裏ラベルファイルをアップロードしてください',
    error_generalError: 'エラー',
    error_noProductOrLot: '商品またはロット数が選択されていません',
    error_noVariantFound: '選択されたロット数に対応する商品が見つかりません',
    error_restoreError: '復元エラー',
    error_orderRestartError: '注文の再開中にエラーが発生しました',
    error_saveDataError: '保存データの確認エラー',
    error_validationError: 'データの検証中にエラーが発生しました',
    error_dataNotFound: '保存データが見つかりません',
    error_restoreProcessError: '復元中にエラーが発生しました',
    
    // 成功メッセージ（追加）
    success_orderAdded: '注文がカートに追加されました！カート画面に遷移します。',
    success_progressSaved: '注文進捗を保存しました',
    success_dataRestored: '注文進捗を復元しました',
    success_additionalItemsAdded: '追加商品の追加完了',
    success_saveDataDeleted: '保存データを削除しました',
    
    // 顧客情報関連
    customerInfo_loginRequired: 'ログインが必要です',
    customerInfo_approvalPending: '※本会員の登録が完了後、次のステップへ進めます。\n※本会員登録の完了は、3営業日以内にメールにてお知らせいたします。誠に恐れ入りま\nすが、完了通知のメールが届きましたら、改めてご注文手続きを最初から進めていただ\nきますようお願い申し上げます。\n※会員登録がお済みでない方は',
    customerInfo_approvalPendingLink: 'こちら',
    customerInfo_canOrder: '注文可能です',
    customerInfo_statusChecking: '状態を確認中です',
    
    // 価格内訳関連
    priceBreakdown_freezeDriedCoffee: 'フリーズドライコーヒー',
    priceBreakdown_liquidConcentrateCoffee: '濃縮液コーヒー',
    priceBreakdown_straightCoffee: 'ストレートコーヒー',
    priceBreakdown_houseBlend: 'ハウスブレンド',
    priceBreakdown_houseDarkBlend: 'ハウスダークブレンド',
    priceBreakdown_customerBeans: 'お客様の豆',
    priceBreakdown_stick: 'スティック',
    priceBreakdown_cube: 'キューブ',
    priceBreakdown_spout: 'スパウト',
    priceBreakdown_pouch: '個包装',
    priceBreakdown_bottle: 'ボトル',
    priceBreakdown_sugarConcentration: '砂糖濃度',
    priceBreakdown_innerDesignStick: 'インナーデザイン (スティック)',
    priceBreakdown_innerFrontLabelCube: 'インナー表ラベル (キューブ)',
    priceBreakdown_frontLabelDesign: '表ラベルデザイン',
    priceBreakdown_outerBoxWithDesign: '外箱 (デザインあり)',
    priceBreakdown_outerBoxPlain: '外箱 (無地)',
    priceBreakdown_backLabelDesign: '裏ラベルデザイン',
    priceBreakdown_pieces: '個',
    
    // PriceDisplay関連
    priceDisplay_loginPromptTitle: '価格を確認するにはログインが必要です',
    priceDisplay_loginPromptDescription: '会員登録またはログインして価格情報をご確認ください',
    priceDisplay_details: '詳細',
    
    // FlowFooter関連
    flowFooter_back: '戻る',
    flowFooter_next: '次へ',
    flowFooter_saveAndNext: '保存して次へ',
    
    // SelectLotSize関連
    selectLotSize_selectPlaceholder: 'ロット数を選択してください',
    selectLotSize_selectLabel: 'ロット数を選択',
    selectLotSize_manufacturingNote: '※選択したロット数での製造となります',
    selectLotSize_lotDescription100: '小ロットでの試作や少量生産に最適です。',
    selectLotSize_lotDescription250: 'スタンダードなロット数です。',
    selectLotSize_lotDescription500: '中規模での生産に適したロット数です。',
    selectLotSize_lotDescription1000: '大量生産に適した大ロットです。',
    selectLotSize_lotDescription60: '小ロットでの試作や少量生産に最適です。',
    selectLotSize_lotDescription180: 'スタンダードなロット数です。',
    selectLotSize_lotDescription540: '中規模での生産に適したロット数です。',
    selectLotSize_lotDescription1080: '大量生産に適した大ロットです。',
    selectLotSize_defaultDescription: 'デフォルトロット数です。',
    selectLotSize_pieces100: '100個',
    selectLotSize_pieces250: '250個',
    selectLotSize_pieces500: '500個',
    selectLotSize_pieces1000: '1000個',
    selectLotSize_pieces60: '60個',
    selectLotSize_pieces180: '180個',
    selectLotSize_pieces540: '540個',
    selectLotSize_pieces1080: '1080個',
    
    // DesignSubmission関連
    designSubmission_outerBoxTemplateTitle: '外箱デザインテンプレートを選択',
    designSubmission_outerBoxUploadTitle: '外箱デザインアップロード',
    designSubmission_innerDesignTemplateTitle: 'インナーデザインテンプレートを選択',
    designSubmission_innerDesignUploadTitle: 'インナーデザインアップロード',
    designSubmission_innerPackageTemplateTitle: 'インナーパッケージデザインテンプレートを選択',
    designSubmission_innerPackageUploadTitle: 'インナーパッケージデザインアップロード',
    designSubmission_frontLabelTemplateTitle: '表ラベルテンプレートを選択',
    designSubmission_frontLabelUploadTitle: '表ラベルアップロード',
    designSubmission_backLabelSelectionTitle: '裏ラベルの要否を選択',
    designSubmission_backLabelUploadTitle: '裏ラベルアップロード',
    designSubmission_originalDesignCase: 'オリジナルデザインの場合：',
    designSubmission_templateDownload: 'テンプレートのダウンロード',
    designSubmission_companyDesignCase: '弊社準備のデザインを使用する場合：',
    designSubmission_dataDownload: 'データのダウンロード',
    designSubmission_backLabelRequired: '必要',
    designSubmission_backLabelNotRequired: '不要',
    designSubmission_backLabelRequiredDesc: '裏ラベルを追加します',
    designSubmission_backLabelNotRequiredDesc: '裏ラベルは使用しません',
    
    // ファイル関連
    file_outerBoxDesign: '外箱デザイン',
    file_innerDesign: 'インナーデザイン',
    file_frontLabel: '表ラベル',
    file_backLabel: '裏ラベル',
    file_outerBoxDesignFile: '外箱デザインファイル',
    file_innerDesignFile: 'インナーデザインファイル',
    file_frontLabelFile: '表ラベルファイル',
    file_backLabelFile: '裏ラベルファイル',
    
    // Shopify API関連
    shopify_productType: '商品タイプ',
    shopify_beanType: '豆の種類',
    shopify_packageType: 'パッケージタイプ',
    shopify_lotSize: 'ロット数',
    shopify_sugarConcentration: '砂糖濃度',
    shopify_innerDesign: 'インナーデザイン',
    shopify_frontLabel: '表ラベル',
    shopify_outerBoxRequired: '外箱',
    shopify_outerBoxWithDesign: '必要（デザイン有り）',
    shopify_outerBoxWithoutDesign: '必要（デザイン無し）',
    shopify_backLabel: '裏ラベル',
    shopify_orderType: '注文タイプ',
    shopify_customOrder: 'カスタムオーダー',
    shopify_orderDateTime: '注文日時',
    shopify_fileUploadError: 'JPG、PNG、PDF、またはAIファイルのみアップロード可能です。',
    shopify_required: '必要',
    
    // コンソール・ログ関連（開発用）
    console_addingAdditionalItems: '追加商品をカートに追加中',
    console_additionalItemsAdded: '追加商品の追加完了',
    console_orderFlowStart: '注文フロー開始',
    console_viewUsageGuide: 'ご利用方法を見る',
    console_cartAddError: 'カート追加エラー',
    console_cartAddFailed: 'カートへの追加に失敗しました',
    console_cartInfoFailed: 'カート情報の取得に失敗しました',
    console_cartUpdateFailed: 'カートの更新に失敗しました',
    console_cartClearFailed: 'カートのクリアに失敗しました',
    console_saveDataCheckError: '保存データの確認エラー',
    console_dataValidationError: 'データ検証エラー',
    console_restoreError: '復元エラー',
    console_saveDataDeleteError: '保存データ削除エラー',
    
    // OrderConfirmation
    orderConfirm_orderRequiresApproval: 'ご注文には承認が必要です。承認完了までお待ちください。',
    orderConfirm_missingProductInfo: '必要な商品情報が不足しています',
    orderConfirm_fileErrors: 'ファイルエラー:\n',
    orderConfirm_noMatchingVariant: '選択されたロット数に対応する商品が見つかりません',
    orderConfirm_orderProcessingError: '注文処理エラー:',
    orderConfirm_unexpectedError: '予期しないエラーが発生しました',
    orderConfirm_loginRequired: 'ログインが必要です',
    orderConfirm_loginRequiredMessage: 'ご注文にはログインが必要です。',
    orderConfirm_pendingApproval: '承認待ち',
    orderConfirm_pendingApprovalMessage: '現在承認手続き中です。承認完了後にご注文いただけます。',
    orderConfirm_addingToCart: 'カートに追加中...',
    orderConfirm_proceedToOrder: 'ご注文手続きへ',
    orderConfirm_cannotOrder: 'ご注文いただけません',
    orderConfirm_cannotOrderMessage: 'ご注文に必要な条件が満たされていません。',
    orderConfirm_orderComplete: 'オリジナルコーヒー完成',
    
    // 商品タイプ
    productType_freezeDried: 'フリーズドライコーヒー',
    productType_liquidConcentrate: '濃縮液コーヒー',
    productType_straightCoffee: 'ストレートコーヒー',
    
    // 豆の種類
    bean_houseBlend: 'ハウスブレンド',
    bean_houseDarkBlend: 'ハウスダークブレンド',
    bean_customerBean: 'お客様の豆',
    
    // パッケージタイプ
    package_stick: 'スティック',
    package_cube: 'キューブ',
    package_spout: 'スパウト',
    package_pouch: '個包装',
    package_bottle: 'ボトル',
    
    // デザインオプション
    design_innerDesignStick: 'インナーデザイン (スティック)',
    design_innerFrontLabelCube: 'インナー表ラベル (キューブ)',
    design_frontLabelDesign: '表ラベルデザイン',
    design_outerBoxWithDesign: '外箱 (デザインあり)',
    design_outerBoxPlain: '外箱 (無地)',
    design_backLabelDesign: '裏ラベルデザイン',
    
    // ProductPreview
    preview_beanType: '豆の種類',
    preview_sugarOption: '砂糖オプション',
    preview_innerDesign: 'インナーデザイン',
    preview_innerFrontLabel: 'インナー表ラベル',
    preview_outerBox: '外箱',
    preview_outerBoxDesign: '外箱デザイン',
    preview_frontLabel: '表ラベル',
    preview_backLabel: '裏ラベル',
    preview_packageType: '包装タイプ',
    preview_selectProduct: '商品を選択してください',
    preview_finalFallback: '最終フォールバック画像',
    preview_noPreviewData: 'プレビューデータが見つかりません',
    
    // DesignSubmission
    design_innerDesignFile: 'インナーデザインファイル',
    design_frontLabelFile: '表ラベルファイル',
    design_backLabelRequired: '裏ラベル必要',
    design_backLabelFile: '裏ラベルファイル',
    design_backLabelNotRequired: '裏ラベル不要',
    
    // ProductDataManager
    productData_configNotFound: '商品フロー設定データが見つかりません',
    
    // SelectPackageOptions
    selectPackage_productTypeNotSelected: '商品タイプが選択されていません',
    
    // FileUpload
    fileUpload_selectFile: 'ファイルを選択',
    fileUpload_fileSizeExceeded: 'ファイルサイズは{maxSize}MB以下にしてください。現在のサイズ: {currentSize}MB',
    fileUpload_invalidFileType: 'JPG、PNG、PDF、またはAIファイルのみアップロード可能です。',
    fileUpload_invalidFile: 'ファイルが無効です',
    fileUpload_uploadHere: 'ファイルをこちらにアップロードしてください',
    fileUpload_fileTypeInfo: 'JPG、PNG、またはPDF、ファイルサイズは{maxSize}MB以下',
    fileUpload_removeFile: '削除',
    fileUpload_currentSize: '現在のサイズ:',
    fileUpload_mb: 'MB',
    
    // SelectCoffeeBean - 追加の翻訳キー
    selectCoffeeBean_ownBean_label: '自分の豆を使う',
    selectCoffeeBean_ownBean_desc: 'お客様がご用意されたコーヒー豆を使用します。',
    selectCoffeeBean_wakoBean_label: 'WAKOの豆を使う',
    selectCoffeeBean_wakoBean_desc: 'WAKOが厳選したコーヒー豆を使用します。',
    
    // パッケージタイプ - 個包装
    selectPackage_pouch_label: '個包装',
    selectPackage_pouch_desc: '個別包装で持ち運びやすいタイプです。',
    
    // SelectCoffeeBean - 選択状態表示
    selectCoffeeBean_currentSelection: '選択中:',
    selectCoffeeBean_beanType: '豆の種類:',
    selectCoffeeBean_ownBean: '自分の豆',
    selectCoffeeBean_wakoBean: 'WAKOの豆',
    selectCoffeeBean_variety: '銘柄:',
    selectCoffeeBean_innerPackage: 'インナーパッケージ:',
    selectCoffeeBean_packaging: 'パッキング:',
    selectCoffeeBean_sugarConcentration: '砂糖濃度:',
    
    // SelectCoffeeBean - デバッグ情報
    selectCoffeeBean_debugInfo: '銘柄選択デバッグ情報',
    
    // useOrderRestart - エラーメッセージ
    orderRestart_saveDataCheckError: '保存データの確認エラー:',
    orderRestart_savedProductNotFound: '保存された商品が見つかりません',
    orderRestart_savedLotNotAvailable: '保存されたロット数が利用できません',
    orderRestart_productInfoNotSaved: '商品情報が保存されていません',
    orderRestart_requiredSelectionMissing: '必要な選択情報が不足しています',
    orderRestart_dataValidationError: 'データ検証エラー:',
    orderRestart_validationError: 'データの検証中にエラーが発生しました',
    orderRestart_saveDataNotFound: '保存データが見つかりません',
    orderRestart_restoreError: '復元中にエラーが発生しました',
    
    // useOrderRestart - 成功メッセージ
    orderRestart_restoreSuccess: '✅ 注文進捗を復元しました:',
    orderRestart_deleteSuccess: '✅ 保存データを削除しました',
    orderRestart_noDataToDelete: 'ℹ️ 削除対象の保存データはありませんでした',
    orderRestart_deleteError: '❌ 保存データ削除エラー:',
    
    // 共通テキスト
    common_none: 'なし',
    
    // FlowLanding - アクセシビリティ
    flowLanding_usageGuideAriaLabel: 'ご利用方法を詳しく見る',
    
    // OrderFlowContainer - エラーメッセージ
    orderFlow_productDataLoadError: '商品データの読み込みに失敗しました。ページを再読み込みしてください。',
    orderFlow_saveProgressError: '途中保存エラー:',
    orderFlow_orderStartMessage: '注文手続きを開始します',
    
    // OrderConfirmation - リスタートボタン
    orderConfirm_restartFromBeginning: 'もう一度最初から',
    
    // DesignSubmission - 裏ラベル関連（追加）
    designSubmission_backLabelBulkDisplayTitle: '裏ラベル（一括表示）',
    designSubmission_backLabelTemplateSelectionTitle: '裏ラベルデザインテンプレートを選択',

    // DesignSubmission - 共通選択肢
    designSubmission_required: '必要',
    designSubmission_notRequired: '不要',
    designSubmission_requiredDescription: 'オリジナルデザインを使用します',
    designSubmission_notRequiredDescription: '裏ラベルは貼付しません',

    // ヘルプテキスト（日本語）
    help_selectCoffeeBean: 'あなたの豆をWAKOに送るかWAKOの豆を使うかを選択してください。',
    help_selectCoffeeBeanVariety: 'WAKOで取り扱っているコーヒー豆の銘柄からお選びください。',
    help_selectOuterBox: '外箱が必要かどうかを選択してください。',
    help_selectOuterBoxDesign: '外箱デザインが必要かどうかを選択してください。',
    help_selectInnerDesign: 'インナーパッケージのデザインが必要かどうかを選択してください。',
    help_selectInnerFrontLabel: 'インナーパッケージの表ラベルが必要かどうかを選択してください。',
    help_selectFrontLabel: '表ラベルが必要かどうかを選択してください。',
    help_selectLotSize: 'ご希望の発注ロット数を選択してください。ロット数により価格が変わります。',
    help_selectProcessType: 'ご希望の加工形態を選択してください。',
    help_selectPackageFreezeDried: 'フリーズドライの梱包の種類を選択してください。',
    help_selectPackageLiquidConcentrate: '手軽な「ボトル」と、環境にやさしい「スパウトパウチ」の2種類のパッケージがございます。それぞれの特徴をご理解いただき、あなたのライフスタイルや利用シーンにぴったりのパッケージをお選びください。',
    help_selectPackageStraightCoffee: '便利な「個包装」と、環境にやさしい「スパウトパウチ」の2種類のパッケージがございます。それぞれの特徴をご理解いただき、あなたのライフスタイルや利用シーンにぴったりのパッケージをお選びください。',
    help_selectInnerPackage: 'インナーパッケージの種類を選択してください。スティックまたはキューブからお選びください。',
    help_selectSugar: 'コーヒーに含める砂糖の有無を選択してください。',
    help_outerBoxDesignTemplate: 'デザインはGoogleドライブよりダウンロードいただけます。',
    help_outerBoxDesignUpload: 'デザインいただいたものをアップロードしてください。',
    help_innerPackageDesignTemplate: 'デザインはGoogleドライブよりダウンロードいただけます。',
    help_innerPackageDesignUpload: 'デザインいただいたものをアップロードしてください。',
    help_frontLabelDesignTemplate: 'デザインはGoogleドライブよりダウンロードいただけます。',
    help_frontLabelDesignUpload: 'デザインいただいたものをアップロードしてください。',
    help_backLabelSelection: '国ごとに必要な情報を記載してください。',
    help_backLabelDesignTemplate: 'デザインはGoogleドライブよりダウンロードいただけます。',
    help_backLabelDesignUpload: 'デザインいただいたものをアップロードしてください。',

    // 一時的制約メッセージ
    temporaryUnavailable_message: '現在選択できません',

    // 再開ブロックモーダル
    restrictionModal_title: '再開できません',
    restrictionModal_message: '前回の注文内容に、現在選択できないオプションが含まれています。\n恐れ入りますが、新規でご注文をお願いいたします。',
    restrictionModal_startNew: '新規で注文を始める',
    restrictionModal_cancel: 'キャンセル',

  };

  // 英語用デフォルト翻訳
  const englishTranslations: WakoFlowTranslations = {
    flowLandingDescription: 'Create the perfect original coffee for your cafe or brand with WAKO COFFEE LAB.',
    flowLandingDescriptionSp: 'At WAKO COFFEE LAB., \nlet\'s bring your cafe or brand\'s unique original coffee to life.',
    flowLandingDescriptionPc: 'At WAKO COFFEE LAB., \nlet\'s bring your cafe or brand\'s unique original coffee to life.',
    flowLandingNotice: '※If you are creating an original design, please prepare the design data in advance.',
    flowLandingUsageGuideLink: 'View detailed usage instructions',
    flowLandingOrderStartButton: 'ORDER START',
    logoAlt: 'WAKO COFFEE LAB.',
    step1Title: 'Select Processing Type',
    step2Title: 'Select Coffee Bean Type',
    stepIndicator_step1_sp: 'Select\nProcess Type',
    stepIndicator_step1_pc: 'Select Process Type',
    stepIndicator_step2_sp: 'Select\nCoffee Beans',
    stepIndicator_step2_pc: 'Select Coffee Beans',
    stepIndicator_step3_sp: 'Select\nPackage',
    stepIndicator_step3_pc: 'Select Package',
    stepIndicator_step4_sp: 'Select\nOrder Qty',
    stepIndicator_step4_pc: 'Select Order Qty',
    stepIndicator_step5_sp: 'Upload\nDesign',
    stepIndicator_step5_pc: 'Upload Design',

    // SelectProcessType
    selectProcessType_title: 'Select Process Type',
    selectProcessType_option1_label: 'Freeze-dried',
    selectProcessType_option1_desc: 'Description for freeze-dried process goes here. Description for freeze-dried process goes here.',
    selectProcessType_option2_label: 'Liquid-concentrate',
    selectProcessType_option2_desc: 'Description for liquid concentrate goes here. Description for liquid concentrate goes here.',
    selectProcessType_option3_label: 'Straight-coffee',
    selectProcessType_option3_desc: 'Description for straight coffee goes here. Description for straight coffee goes here.',
    
    // SelectCoffeeBean
    selectCoffeeBean_title: 'Select Coffee Bean Type',
    selectCoffeeBean_option1_label: 'Use Your Own Beans',
    selectCoffeeBean_option1_desc: 'Please send us the coffee beans you regularly use.',
    selectCoffeeBean_option2_label: "Use WAKO's Beans",
    selectCoffeeBean_option2_desc: 'Please choose from the coffee beans we handle at WAKO.',
    selectCoffeeBean_cardSelector_title: 'Select Bean Type',
    selectCoffeeBean_varietyTitle: 'Select Variety',
    selectCoffeeBean_bean1_label: 'House Blend',
    selectCoffeeBean_bean1_price: '(328JPY/per 100g)',
    selectCoffeeBean_bean1_desc: 'A daily-use blend of Brazilian and Colombian beans, light and easy to drink, crafted to be enjoyed every day.',
    selectCoffeeBean_bean2_label: 'House Dark Blend',
    selectCoffeeBean_bean2_price: '(328JPY/per 100g)',
    selectCoffeeBean_bean2_desc: 'A deep-roasted blend with a fragrant aroma and rich, bitter notes.',
    
    // パッキング種類選択（濃縮液・ストレート用）
    selectPackage_title: 'Select Package Type',
    selectPackage_spout_label: 'Spout',
    selectPackage_spout_desc: 'Lightweight and portable spout pouch type.',
    selectPackage_individualPackaging_label: 'Individual Packaging',
    selectPackage_individualPackaging_desc: 'Individual packaging for easy portability.',
    selectPackage_bottle_label: 'Bottle',
    selectPackage_bottle_desc: 'Excellent sealing bottle type.',
    selectPackage_region_restriction_notice: '※Shipping restrictions may apply to some regions.',
    
    // STEP4: Lot Size Selection
    selectLotSize_title: 'Order Lot Size',
    selectLotSize_description: 'Please select your desired lot size. Price varies by lot size.',
    selectLotSize_notice: '※1 lot = 100 pieces',
    selectLotSize_saveNotice1: '※Pressing "Save and Next" will save your progress up to this point.',
    selectLotSize_saveNotice2: 'You can continue creating from TOP page afterwards.',
    priceDisplay_priceOnRequest: 'Price on request',
    
    // インナーパッケージ選択（フリーズドライ用）
    selectInnerPackage_title: 'Select Inner Package',
    selectInnerPackage_stick_label: 'Stick',
    selectInnerPackage_stick_desc: 'Individual stick packaging type.',
    selectInnerPackage_cube_label: 'Cube',
    selectInnerPackage_cube_desc: 'Compact cube type.',
    
    // 砂糖濃度選択（濃縮液・ストレート用）
    selectSugar_title: 'Select Sugar Concentration',
    selectSugar_none_label: 'No Sugar',
    selectSugar_none_desc: 'No sugar added.',
    selectSugar_3_label: '3%',
    selectSugar_3_desc: 'Lightly sweetened type.',
    selectSugar_6_label: '6%',
    selectSugar_6_desc: 'Mildly sweet type.',
    selectSugar_9_label: '9%',
    selectSugar_9_desc: 'Balanced sweetness.',
    selectSugar_12_label: '12%',
    selectSugar_12_desc: 'Rich sweetness.',
    selectSugar_18_label: '18%',
    selectSugar_18_desc: 'Sweet type.',
    selectSugar_24_label: '24%',
    selectSugar_24_desc: 'High sugar content type.',
    
    // 価格表示
    priceDisplay_basePrice: 'Base Price',
    priceDisplay_optionPrice: 'Option Price',
    priceDisplay_totalPrice: 'Total Price',
    priceDisplay_currency: '$',
    priceDisplay_taxIncluded: 'Tax Included',
    priceDisplay_perLot: 'per lot',
    
    // 共通アクション
    action_next: 'Next',
    action_back: 'Back',
    action_confirm: 'Confirm',
    action_addToCart: 'Add to Cart',
    action_select: 'Select',
    action_skip: 'Skip',
    action_retry: 'Reload Page',
    
    // エラーメッセージ
    error_selectRequired: 'Please select coffee bean type',
    error_selectVariety: 'Please select variety',
    error_selectInnerPackage: 'Please select inner package',
    error_selectPackage: 'Please select package type',
    error_selectSugar: 'Please select sugar concentration',
    error_fileRequired: 'File required',
    error_invalidFile: 'Invalid file',
    error_priceCalculation: 'Price calculation error occurred',
    
    // 成功メッセージ
    success_addedToCart: 'Added to cart',
    success_fileUploaded: 'File uploaded',
    
    // フォーム関連
    form_selectOption: 'Select option',
    form_uploadFile: 'Upload file',
    form_fileName: 'File name',
    form_fileSize: 'File size',
    
    // パッケージタイプ
    packageType_stick: 'Stick',
    packageType_cube: 'Cube',
    packageType_spout: 'Spout',
    packageType_bottle: 'Bottle',
    packageType_individualPackaging: 'Individual Packaging',
    
    // STEP3: パッケージオプション選択
    selectPackageOptions_title: 'Select Package Options',
    
    // フリーズドライ用 - インナーパッケージ関連
    selectInnerDesign_title: 'Inner Package Design',
    selectInnerDesign_required_label: 'Required',
    selectInnerDesign_required_desc: 'Use original design',
    selectInnerDesign_notRequired_label: 'Not Required',
    selectInnerDesign_notRequired_desc: 'Use standard design',
    
    selectInnerFrontLabel_title: 'Do you need a front label for the inner package?',
    selectInnerFrontLabel_required_label: 'Required',
    selectInnerFrontLabel_required_desc: 'Add front label',
    selectInnerFrontLabel_notRequired_label: 'Not Required',
    selectInnerFrontLabel_notRequired_desc: 'No front label',
    
    // 外箱関連（全フロー共通）
    selectOuterBox_title: 'Outer Box',
    selectOuterBox_required_label: 'Required',
    selectOuterBox_required_desc: 'Add outer box',
    selectOuterBox_notRequired_label: 'Not Required',
    selectOuterBox_notRequired_desc: 'No outer box',
    
    selectOuterBoxDesign_title: 'Outer Box Design',
    selectOuterBoxDesign_required_label: 'Required',
    selectOuterBoxDesign_required_desc: 'Use original design',
    selectOuterBoxDesign_notRequired_label: 'Not Required',
    selectOuterBoxDesign_notRequired_desc: 'Use standard design',
    
    // 濃縮液・ストレート用 - 表ラベル関連
    selectFrontLabel_title: 'Front Label',
    selectFrontLabel_required_label: 'Required',
    selectFrontLabel_required_desc: 'Add front label',
    selectFrontLabel_notRequired_label: 'Not Required',
    selectFrontLabel_notRequired_desc: 'No front label',
    
    // 価格表示（オプション用）
    priceDisplay_optionAdditional: 'Additional Fee',
    priceDisplay_estimatedPrice: 'Estimated Price',
    
    // 新しく追加する翻訳データ（英語）
    
    // エラーメッセージ（追加）
    error_uploadInnerDesign: 'Please upload inner design file',
    error_uploadFrontLabel: 'Please upload front label file',
    error_uploadOuterBoxDesign: 'Please upload outer box design file',
    error_uploadBackLabel: 'Please upload back label file',
    error_generalError: 'Error',
    error_noProductOrLot: 'Product or lot size not selected',
    error_noVariantFound: 'No variant found for selected lot size',
    error_restoreError: 'Restore Error',
    error_orderRestartError: 'Error occurred during order restart',
    error_saveDataError: 'Save data check error',
    error_validationError: 'Error occurred during data validation',
    error_dataNotFound: 'Save data not found',
    error_restoreProcessError: 'Error occurred during restore process',
    
    // 成功メッセージ（追加）
    success_orderAdded: 'Order added to cart! Redirecting to cart page.',
    success_progressSaved: 'Order progress saved',
    success_dataRestored: 'Order progress restored',
    success_additionalItemsAdded: 'Additional items added successfully',
    success_saveDataDeleted: 'Save data deleted',
    
    // 顧客情報関連
    customerInfo_loginRequired: 'Login required',
    customerInfo_approvalPending: '※You can proceed to the next step after full membership registration is completed.\n※Full membership registration completion will be notified by email within 3 business days. We apologize for the inconvenience, but once you receive the completion notification email, please start the order process from the beginning again.\n※For those who have not completed membership registration, please ',
    customerInfo_approvalPendingLink: 'click here',
    customerInfo_canOrder: 'Can place order',
    customerInfo_statusChecking: 'Checking status',
    
    // 価格内訳関連
    priceBreakdown_freezeDriedCoffee: 'Freeze-dried Coffee',
    priceBreakdown_liquidConcentrateCoffee: 'Liquid Concentrate Coffee',
    priceBreakdown_straightCoffee: 'Straight Coffee',
    priceBreakdown_houseBlend: 'House Blend',
    priceBreakdown_houseDarkBlend: 'House Dark Blend',
    priceBreakdown_customerBeans: 'Customer Beans',
    priceBreakdown_stick: 'Stick',
    priceBreakdown_cube: 'Cube',
    priceBreakdown_spout: 'Spout',
    priceBreakdown_pouch: 'Individual Packaging',
    priceBreakdown_bottle: 'Bottle',
    priceBreakdown_sugarConcentration: 'Sugar Concentration',
    priceBreakdown_innerDesignStick: 'Inner Design (Stick)',
    priceBreakdown_innerFrontLabelCube: 'Inner Front Label (Cube)',
    priceBreakdown_frontLabelDesign: 'Front Label Design',
    priceBreakdown_outerBoxWithDesign: 'Outer Box (With Design)',
    priceBreakdown_outerBoxPlain: 'Outer Box (Plain)',
    priceBreakdown_backLabelDesign: 'Back Label Design',
    priceBreakdown_pieces: 'pieces',
    
    // PriceDisplay関連
    priceDisplay_loginPromptTitle: 'Login required to view pricing',
    priceDisplay_loginPromptDescription: 'Please register or login to view pricing information',
    priceDisplay_details: 'Details',
    
    // FlowFooter関連
    flowFooter_back: 'Back',
    flowFooter_next: 'Next',
    flowFooter_saveAndNext: 'Save and Next',
    
    // SelectLotSize関連
    selectLotSize_selectPlaceholder: 'Please select lot size',
    selectLotSize_selectLabel: 'Select lot size',
    selectLotSize_manufacturingNote: '※Manufacturing will be based on selected lot size',
    selectLotSize_lotDescription100: 'Ideal for small lot prototyping or small-scale production.',
    selectLotSize_lotDescription250: 'Standard lot size.',
    selectLotSize_lotDescription500: 'Suitable for medium-scale production.',
    selectLotSize_lotDescription1000: 'Large lot suitable for mass production.',
    selectLotSize_lotDescription60: 'Ideal for small lot prototyping or small-scale production.',
    selectLotSize_lotDescription180: 'Standard lot size.',
    selectLotSize_lotDescription540: 'Suitable for medium-scale production.',
    selectLotSize_lotDescription1080: 'Large lot suitable for mass production.',
    selectLotSize_defaultDescription: 'Default lot size.',
    selectLotSize_pieces100: '100 pieces',
    selectLotSize_pieces250: '250 pieces',
    selectLotSize_pieces500: '500 pieces',
    selectLotSize_pieces1000: '1000 pieces',
    selectLotSize_pieces60: '60 pieces',
    selectLotSize_pieces180: '180 pieces',
    selectLotSize_pieces540: '540 pieces',
    selectLotSize_pieces1080: '1080 pieces',
    
    // DesignSubmission関連
    designSubmission_outerBoxTemplateTitle: 'Select Outer Box Design Template',
    designSubmission_outerBoxUploadTitle: 'Upload Outer Box Design',
    designSubmission_innerDesignTemplateTitle: 'Select Inner Design Template',
    designSubmission_innerDesignUploadTitle: 'Upload Inner Design',
    designSubmission_innerPackageTemplateTitle: 'Select inner package design template',
    designSubmission_innerPackageUploadTitle: 'Upload inner package design',
    designSubmission_frontLabelTemplateTitle: 'Select Front Label Template',
    designSubmission_frontLabelUploadTitle: 'Upload Front Label',
    designSubmission_backLabelSelectionTitle: 'Select Back Label Requirement',
    designSubmission_backLabelUploadTitle: 'Upload Back Label',
    designSubmission_originalDesignCase: 'For original design:',
    designSubmission_templateDownload: 'Download Template',
    designSubmission_companyDesignCase: 'To use our prepared design:',
    designSubmission_dataDownload: 'Download Data',
    designSubmission_backLabelRequired: 'Required',
    designSubmission_backLabelNotRequired: 'Not Required',
    designSubmission_backLabelRequiredDesc: 'Add back label',
    designSubmission_backLabelNotRequiredDesc: 'No back label',
    
    // ファイル関連
    file_outerBoxDesign: 'Outer Box Design',
    file_innerDesign: 'Inner Design',
    file_frontLabel: 'Front Label',
    file_backLabel: 'Back Label',
    file_outerBoxDesignFile: 'Outer Box Design File',
    file_innerDesignFile: 'Inner Design File',
    file_frontLabelFile: 'Front Label File',
    file_backLabelFile: 'Back Label File',
    
    // Shopify API関連
    shopify_productType: 'Product Type',
    shopify_beanType: 'Bean Type',
    shopify_packageType: 'Package Type',
    shopify_lotSize: 'Lot Size',
    shopify_sugarConcentration: 'Sugar Concentration',
    shopify_innerDesign: 'Inner Design',
    shopify_frontLabel: 'Front Label',
    shopify_outerBoxRequired: 'Outer Box',
    shopify_outerBoxWithDesign: 'Required (With Design)',
    shopify_outerBoxWithoutDesign: 'Required (Without Design)',
    shopify_backLabel: 'Back Label',
    shopify_orderType: 'Order Type',
    shopify_customOrder: 'Custom Order',
    shopify_orderDateTime: 'Order Date Time',
    shopify_fileUploadError: 'Only JPG, PNG, PDF, or AI files can be uploaded.',
    shopify_required: 'Required',
    
    // コンソール・ログ関連（開発用）
    console_addingAdditionalItems: 'Adding additional items to cart',
    console_additionalItemsAdded: 'Additional items added successfully',
    console_orderFlowStart: 'Order flow started',
    console_viewUsageGuide: 'View usage guide',
    console_cartAddError: 'Cart add error',
    console_cartAddFailed: 'Failed to add to cart',
    console_cartInfoFailed: 'Failed to get cart information',
    console_cartUpdateFailed: 'Failed to update cart',
    console_cartClearFailed: 'Failed to clear cart',
    console_saveDataCheckError: 'Save data check error',
    console_dataValidationError: 'Data validation error',
    console_restoreError: 'Restore error',
    console_saveDataDeleteError: 'Save data delete error',
    
    // OrderConfirmation
    orderConfirm_orderRequiresApproval: 'Order requires approval. Please wait for approval to complete.',
    orderConfirm_missingProductInfo: 'Required product information is missing',
    orderConfirm_fileErrors: 'File errors:\n',
    orderConfirm_noMatchingVariant: 'No product found for selected lot size',
    orderConfirm_orderProcessingError: 'Order processing error:',
    orderConfirm_unexpectedError: 'An unexpected error occurred',
    orderConfirm_loginRequired: 'Login required',
    orderConfirm_loginRequiredMessage: 'Login is required to place an order.',
    orderConfirm_pendingApproval: 'Pending approval',
    orderConfirm_pendingApprovalMessage: 'Currently under approval process. You can place an order after approval is complete.',
    orderConfirm_addingToCart: 'Adding to cart...',
    orderConfirm_proceedToOrder: 'Proceed to order',
    orderConfirm_cannotOrder: 'Cannot place order',
    orderConfirm_cannotOrderMessage: 'Required conditions for ordering are not met.',
    orderConfirm_orderComplete: 'Original coffee complete',
    
    // 商品タイプ
    productType_freezeDried: 'Freeze-dried Coffee',
    productType_liquidConcentrate: 'Liquid Concentrate Coffee',
    productType_straightCoffee: 'Straight Coffee',
    
    // 豆の種類
    bean_houseBlend: 'House Blend',
    bean_houseDarkBlend: 'House Dark Blend',
    bean_customerBean: 'Customer Beans',
    
    // パッケージタイプ
    package_stick: 'Stick',
    package_cube: 'Cube',
    package_spout: 'Spout',
    package_pouch: 'Individual Packaging',
    package_bottle: 'Bottle',
    
    // デザインオプション
    design_innerDesignStick: 'Inner Design (Stick)',
    design_innerFrontLabelCube: 'Inner Front Label (Cube)',
    design_frontLabelDesign: 'Front Label Design',
    design_outerBoxWithDesign: 'Outer Box (With Design)',
    design_outerBoxPlain: 'Outer Box (Plain)',
    design_backLabelDesign: 'Back Label Design',
    
    // ProductPreview
    preview_beanType: 'Bean Type',
    preview_sugarOption: 'Sugar Option',
    preview_innerDesign: 'Inner Design',
    preview_innerFrontLabel: 'Inner Front Label',
    preview_outerBox: 'Outer Box',
    preview_outerBoxDesign: 'Outer Box Design',
    preview_frontLabel: 'Front Label',
    preview_backLabel: 'Back Label',
    preview_packageType: 'Package Type',
    preview_selectProduct: 'Please select a product',
    preview_finalFallback: 'Final fallback image',
    preview_noPreviewData: 'No preview data found',
    
    // DesignSubmission
    design_innerDesignFile: 'Inner Design File',
    design_frontLabelFile: 'Front Label File',
    design_backLabelRequired: 'Back Label Required',
    design_backLabelFile: 'Back Label File',
    design_backLabelNotRequired: 'Back Label Not Required',
    
    // ProductDataManager
    productData_configNotFound: 'Product flow configuration data not found',
    
    // SelectPackageOptions
    selectPackage_productTypeNotSelected: 'Product type not selected',
    
    // FileUpload
    fileUpload_selectFile: 'Select file',
    fileUpload_fileSizeExceeded: 'File size must be {maxSize}MB or less. Current size: {currentSize}MB',
    fileUpload_invalidFileType: 'Only JPG, PNG, PDF, or AI files can be uploaded.',
    fileUpload_invalidFile: 'Invalid file',
    fileUpload_uploadHere: 'Upload your file here',
    fileUpload_fileTypeInfo: 'JPG, PNG, or PDF, file size {maxSize}MB or less',
    fileUpload_removeFile: 'Remove',
    fileUpload_currentSize: 'Current size:',
    fileUpload_mb: 'MB',
    
    // SelectCoffeeBean - 追加の翻訳キー
    selectCoffeeBean_ownBean_label: 'Use Your Own Beans',
    selectCoffeeBean_ownBean_desc: 'Use the coffee beans you have prepared.',
    selectCoffeeBean_wakoBean_label: "Use WAKO's Beans",
    selectCoffeeBean_wakoBean_desc: 'Use coffee beans carefully selected by WAKO.',
    
    // パッケージタイプ - 個包装
    selectPackage_pouch_label: 'Individual Packaging',
    selectPackage_pouch_desc: 'Individual packaging for easy portability.',
    
    // SelectCoffeeBean - 選択状態表示
    selectCoffeeBean_currentSelection: 'Currently Selected:',
    selectCoffeeBean_beanType: 'Bean Type:',
    selectCoffeeBean_ownBean: 'Your Own Beans',
    selectCoffeeBean_wakoBean: "WAKO's Beans",
    selectCoffeeBean_variety: 'Variety:',
    selectCoffeeBean_innerPackage: 'Inner Package:',
    selectCoffeeBean_packaging: 'Packaging:',
    selectCoffeeBean_sugarConcentration: 'Sugar Concentration:',
    
    // SelectCoffeeBean - デバッグ情報
    selectCoffeeBean_debugInfo: 'Variety Selection Debug Info',
    
    // useOrderRestart - エラーメッセージ
    orderRestart_saveDataCheckError: 'Save data check error:',
    orderRestart_savedProductNotFound: 'Saved product not found',
    orderRestart_savedLotNotAvailable: 'Saved lot size not available',
    orderRestart_productInfoNotSaved: 'Product information not saved',
    orderRestart_requiredSelectionMissing: 'Required selection information missing',
    orderRestart_dataValidationError: 'Data validation error:',
    orderRestart_validationError: 'Error occurred during data validation',
    orderRestart_saveDataNotFound: 'Save data not found',
    orderRestart_restoreError: 'Error occurred during restoration',
    
    // useOrderRestart - 成功メッセージ
    orderRestart_restoreSuccess: '✅ Order progress restored:',
    orderRestart_deleteSuccess: '✅ Save data deleted',
    orderRestart_noDataToDelete: 'ℹ️ No save data to delete',
    orderRestart_deleteError: '❌ Save data deletion error:',
    
    // 共通テキスト
    common_none: 'None',
    
    // FlowLanding - アクセシビリティ
    flowLanding_usageGuideAriaLabel: 'View detailed usage guide',
    
    // OrderFlowContainer - エラーメッセージ
    orderFlow_productDataLoadError: 'Failed to load product data. Please reload the page.',
    orderFlow_saveProgressError: 'Save progress error:',
    orderFlow_orderStartMessage: 'Starting order process',
    
    // OrderConfirmation - リスタートボタン
    orderConfirm_restartFromBeginning: 'Start Over',
    
    // DesignSubmission - 裏ラベル関連（追加）
    designSubmission_backLabelBulkDisplayTitle: 'Back Label (Bulk Display)',
    designSubmission_backLabelTemplateSelectionTitle: 'Select Back Label Design Template',

    // DesignSubmission - 共通選択肢
    designSubmission_required: 'Required',
    designSubmission_notRequired: 'Not Required',
    designSubmission_requiredDescription: 'Use original design',
    designSubmission_notRequiredDescription: 'Back label will not be applied',

    // ヘルプテキスト（英語）
    help_selectCoffeeBean: 'You can choose to send your beans to WAKO or receive WAKO\'s beans.', 
    help_selectCoffeeBeanVariety: 'Please select from the coffee bean varieties handled by WAKO.',
    help_selectOuterBox: 'Please select if you need an outer box.',
    help_selectOuterBoxDesign: 'Please select if you need an outer box design.',
    help_selectInnerDesign: 'Please select if you need inner package design.',
    help_selectInnerFrontLabel: 'Please select if you need a front label for the inner package.',
    help_selectFrontLabel: 'Please select if you need a front label.',
    help_selectLotSize: 'Please select your desired order lot size. Price varies by lot size.',
    help_selectProcessType: 'Please select your preferred processing type.',
    help_selectPackageFreezeDried: 'Please select the type of packaging for freeze-dried products.',
    help_selectPackageLiquidConcentrate: 'We offer two types of packaging: the convenient "bottle" and the eco-friendly "spout pouch." Please understand the features of each and choose the package that best fits your lifestyle and usage scenarios.',
    help_selectPackageStraightCoffee: 'We offer two types of packaging: the convenient "individual packaging" and the eco-friendly "spout pouch." Please understand the features of each and choose the package that best fits your lifestyle and usage scenarios.',
    help_selectInnerPackage: 'Please select the inner package type. You can choose from stick or cube.',
    help_selectSugar: 'Please select whether to include sugar in your coffee.',
    help_outerBoxDesignTemplate: 'The design can be downloaded from Google Drive.',
    help_outerBoxDesignUpload: 'Please upload the design you created.',
    help_innerPackageDesignTemplate: 'The design can be downloaded from Google Drive.',
    help_innerPackageDesignUpload: 'Please upload the design you created.',
    help_frontLabelDesignTemplate: 'The design can be downloaded from Google Drive.',
    help_frontLabelDesignUpload: 'Please upload the design you created.',
    help_backLabelSelection: 'Please select if you need a back label.',
    help_backLabelDesignTemplate: 'The design can be downloaded from Google Drive.',
    help_backLabelDesignUpload: 'Please upload the design you created.',

    // Temporary restriction message
    temporaryUnavailable_message: 'Currently unavailable',

    // Restriction modal (resume blocked)
    restrictionModal_title: 'Cannot Resume',
    restrictionModal_message: 'Your previous order contains options that are currently unavailable.\nPlease start a new order.',
    restrictionModal_startNew: 'Start New Order',
    restrictionModal_cancel: 'Cancel',

  };

  // 翻訳データが存在する場合はそれを返す
  if (translations) {
    return translations;
  }
  
  // 翻訳データが存在しない場合は言語に応じたデフォルトを返す
  if (currentLocale === 'ja') {
    return defaultTranslations;
  }
  
  return englishTranslations;
}; 